import type { MedplumClient } from '@medplum/core';
import type { Practice } from '../pages/users/types/rbac';
import { editOrgBrand, editOrgPortal, editOrgSettings } from './adminActionsService';

/**
 * Persists Practice Settings tab changes via the admin-actions bot.
 */
export async function updatePracticeSettings(
  medplum: MedplumClient,
  id: string,
  updates: Partial<Practice>,
): Promise<void> {
  const le = updates.legalEntityAddress;
  const addrLine = [le?.addressLine1, le?.addressLine2].filter(Boolean) as string[];

  const settingsFields: Parameters<typeof editOrgSettings>[2] = {
    ...(updates.name !== undefined ? { name: updates.name } : {}),
    ...(updates.status !== undefined ? { active: updates.status !== 'Inactive' } : {}),
    ...(updates.legalName !== undefined ? { legalName: updates.legalName } : {}),
    ...(updates.taxId !== undefined ? { ein: updates.taxId } : {}),
    ...(updates.npi !== undefined ? { npi: updates.npi } : {}),
    ...((updates.email !== undefined || updates.phone !== undefined)
      ? { contact: { email: updates.email, phone: updates.phone } }
      : {}),
    ...(updates.websiteUrl !== undefined ? { websiteUrl: updates.websiteUrl } : {}),
    ...(updates.cancellationWindowHours !== undefined
      ? { cancellationWindowHours: updates.cancellationWindowHours }
      : {}),
    ...(updates.missedSessionFee !== undefined
      ? { missedSessionFee: updates.missedSessionFee }
      : {}),
    ...(updates.emailResponseTimeframe !== undefined
      ? { emailResponseTimeframe: updates.emailResponseTimeframe }
      : {}),
    ...(le !== undefined
      ? {
          address: {
            ...(addrLine.length > 0 ? { line: addrLine } : {}),
            ...(le.city ? { city: le.city } : {}),
            ...(le.state ? { state: le.state } : {}),
            ...(le.zipCode ? { postalCode: le.zipCode } : {}),
          },
        }
      : {}),
  };

  const promises: Promise<unknown>[] = [];

  if (Object.keys(settingsFields).length > 0) {
    promises.push(editOrgSettings(medplum, id, settingsFields));
  }

  if (updates.branding || updates.bio !== undefined) {
    promises.push(editOrgBrand(medplum, id, {
      primaryColor: updates.branding?.primaryColor,
      logoUrl: updates.branding?.logoUrl,
      secondaryColor: updates.branding?.secondaryColor,
      bio: updates.bio,
    }));
  }

  if (
    updates.portalTiles !== undefined
    || updates.intakeLinkEnabled !== undefined
    || updates.intakeLinkToken !== undefined
    || updates.intakePreference !== undefined
    || updates.directoryEmbedEnabled !== undefined
  ) {
    promises.push(editOrgPortal(medplum, id, {
      intakeLinkEnabled: updates.intakeLinkEnabled,
      intakeLinkToken: updates.intakeLinkToken,
      intakePreference: updates.intakePreference,
      directoryEmbedEnabled: updates.directoryEmbedEnabled,
      tiles: updates.portalTiles,
    }));
  }

  await Promise.all(promises);
}
